import { gqlRequest } from '@mdvip/shared/lib/graphql-client';
import { MyPaymentPlanDocument, type MyPaymentPlanFailureCode } from '@/graphql/generated/graphql';

/** The caller's active billing cadence and annual membership fee. */
export interface PaymentPlan {
  cadence: string;
  annualTotal: number;
}

export const paymentPlanKey = ['payments', 'paymentPlan'] as const;

/** Thrown when myPaymentPlan returns a domain Failure variant other than `NO_ACTIVE_PAYMENT_PLAN`. */
export class PaymentPlanError extends Error {
  constructor(public readonly code: MyPaymentPlanFailureCode) {
    super(code);
    this.name = 'PaymentPlanError';
  }
}

/**
 * `NO_ACTIVE_PAYMENT_PLAN` describes a real, non-error account state (see the "no active plan"
 * empty state in PaymentPlanMetric), so it's swallowed to `null` here rather than thrown — any
 * other Failure code is a real error and is thrown as a typed `PaymentPlanError` so growing the
 * enum doesn't silently fall through to the empty state. Infra/caller errors still propagate as
 * thrown `ClientError`s from gqlRequest.
 */
export async function requestPaymentPlan(): Promise<PaymentPlan | null> {
  const data = await gqlRequest(MyPaymentPlanDocument);
  const result = data.myPaymentPlan;
  if (result.__typename === 'MyPaymentPlanFailure') {
    if (result.code === 'NO_ACTIVE_PAYMENT_PLAN') {
      return null;
    }
    throw new PaymentPlanError(result.code);
  }
  return { cadence: result.cadence, annualTotal: result.annualTotal };
}
